import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  FileText, Upload, Download, Trash2, Eye, Search, Filter,
  RefreshCw, FolderOpen, File, FileCheck, Ship, Plane, Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPES = [
  { value: 'invoice', label: 'Invoice', label_es: 'Factura', icon: FileText },
  { value: 'bill_of_lading', label: 'Bill of Lading', label_es: 'Conocimiento de Embarque', icon: Ship },
  { value: 'certificate', label: 'Certificate', label_es: 'Certificado', icon: FileCheck },
  { value: 'customs', label: 'Customs Document', label_es: 'Documento Aduanero', icon: File },
  { value: 'packing_list', label: 'Packing List', label_es: 'Lista de Empaque', icon: FolderOpen },
  { value: 'insurance', label: 'Insurance', label_es: 'Seguro', icon: FileText },
  { value: 'other', label: 'Other', label_es: 'Otro', icon: File }
];

const DocumentManagementPage = () => {
  const { i18n } = useTranslation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const isSpanish = i18n.language === 'es';
  const isAdmin = user?.role === 'admin' || user?.role === 'employee';
  
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Dialog states
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  // Upload form
  const [uploadForm, setUploadForm] = useState({
    name: '',
    document_type: '',
    description: '',
    file_url: '',
    related_to: '',
    related_type: ''
  });

  useEffect(() => {
    if (isAuthenticated ) {
      fetchDocuments();
    }
  }, [isAuthenticated, token, filterType]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('document_type', filterType);
      
      const res = await fetch(`${API_URL}/api/documents?${params}`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        setDocuments(await res.json());
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error(isSpanish ? 'Error al cargar documentos' : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadForm.name || !uploadForm.document_type || !uploadForm.file_url) {
      toast.error(isSpanish ? 'Complete los campos requeridos' : 'Please fill required fields');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/documents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          
        },
        body: JSON.stringify(uploadForm)
      });
      
      if (res.ok) {
        toast.success(isSpanish ? 'Documento guardado' : 'Document saved successfully');
        setIsUploadDialogOpen(false);
        resetUploadForm();
        fetchDocuments();
      } else {
        toast.error(isSpanish ? 'Error al guardar' : 'Failed to save');
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error de conexión' : 'Connection error');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm(isSpanish ? '¿Eliminar este documento?' : 'Delete this document?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/documents/${docId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success(isSpanish ? 'Documento eliminado' : 'Document deleted');
        fetchDocuments();
      }
    } catch (error) {
      toast.error(isSpanish ? 'Error al eliminar' : 'Failed to delete');
    }
  };

  const resetUploadForm = () => {
    setUploadForm({
      name: '',
      document_type: '',
      description: '',
      file_url: '',
      related_to: '',
      related_type: ''
    });
  };

  const viewDocument = (doc) => {
    setSelectedDocument(doc);
    setIsViewDialogOpen(true);
  };

  const getDocumentTypeInfo = (type) => {
    return DOCUMENT_TYPES.find(t => t.value === type) || DOCUMENT_TYPES[6];
  };

  // Filter documents
  const filteredDocuments = documents.filter(doc => 
    doc.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Auth check
  if (authLoading) {
    return (
      <div className="min-h-screen bg-steel-50 pt-24 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-steel-50 pt-24" data-testid="document-management-page">
      {/* Header */}
      <section className="bg-navy-900 py-12">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
          >
            <div>
              <h1 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-2">
                {isSpanish ? 'Gestión de Documentos' : 'Document Management'}
              </h1>
              <p className="text-steel-300">
                {isSpanish 
                  ? 'Administra facturas, conocimientos de embarque, certificados y más'
                  : 'Manage invoices, bills of lading, certificates, and more'}
              </p>
            </div>
            <Button 
              onClick={() => { resetUploadForm(); setIsUploadDialogOpen(true); }}
              className="btn-accent"
            >
              <Plus className="w-4 h-4 mr-2" />
              {isSpanish ? 'Nuevo Documento' : 'New Document'}
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 bg-white border-b border-steel-200">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex gap-4 items-center w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-steel-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={isSpanish ? 'Buscar documentos...' : 'Search documents...'}
                  className="pl-10"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder={isSpanish ? 'Tipo' : 'Type'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isSpanish ? 'Todos' : 'All Types'}</SelectItem>
                  {DOCUMENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {isSpanish ? type.label_es : type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={fetchDocuments}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {isSpanish ? 'Actualizar' : 'Refresh'}
            </Button>
          </div>
        </div>
      </section>

      {/* Document Stats */}
      <section className="py-6">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {DOCUMENT_TYPES.slice(0, 4).map(type => {
              const count = documents.filter(d => d.document_type === type.value).length;
              const Icon = type.icon;
              return (
                <Card key={type.value} className="border-0 shadow-md">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center">
                        <Icon className="w-5 h-5 text-gold" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-navy-800">{count}</p>
                        <p className="text-sm text-steel-500">{isSpanish ? type.label_es : type.label}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Documents Table */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-gold" />
                {isSpanish ? 'Documentos' : 'Documents'} ({filteredDocuments.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 text-gold animate-spin mx-auto mb-4" />
                  <p className="text-steel-600">{isSpanish ? 'Cargando...' : 'Loading...'}</p>
                </div>
              ) : filteredDocuments.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-steel-300 mx-auto mb-4" />
                  <p className="text-steel-600">{isSpanish ? 'No hay documentos' : 'No documents found'}</p>
                  <Button 
                    onClick={() => setIsUploadDialogOpen(true)} 
                    className="mt-4"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {isSpanish ? 'Subir Documento' : 'Upload Document'}
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{isSpanish ? 'Nombre' : 'Name'}</TableHead>
                        <TableHead>{isSpanish ? 'Tipo' : 'Type'}</TableHead>
                        <TableHead>{isSpanish ? 'Relacionado' : 'Related To'}</TableHead>
                        <TableHead>{isSpanish ? 'Fecha' : 'Date'}</TableHead>
                        <TableHead>{isSpanish ? 'Subido por' : 'Uploaded By'}</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredDocuments.map(doc => {
                        const typeInfo = getDocumentTypeInfo(doc.document_type);
                        const TypeIcon = typeInfo.icon;
                        return (
                          <TableRow key={doc._id || doc.id}>
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-steel-100 rounded flex items-center justify-center">
                                  <TypeIcon className="w-4 h-4 text-steel-600" />
                                </div>
                                <span className="font-medium">{doc.name}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="bg-steel-100 px-2 py-1 rounded text-sm">
                                {isSpanish ? typeInfo.label_es : typeInfo.label}
                              </span>
                            </TableCell>
                            <TableCell className="text-steel-600">
                              {doc.related_to || '-'}
                            </TableCell>
                            <TableCell className="text-steel-500">
                              {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell className="text-steel-500">
                              {doc.uploaded_by || '-'}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => viewDocument(doc)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {doc.file_url && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => window.open(doc.file_url, '_blank')}
                                  >
                                    <Download className="w-4 h-4" />
                                  </Button>
                                )}
                                {isAdmin && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleDeleteDocument(doc.id)}
                                  >
                                    <Trash2 className="w-4 h-4 text-red-500" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isSpanish ? 'Nuevo Documento' : 'New Document'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>{isSpanish ? 'Nombre del Documento' : 'Document Name'} *</Label>
              <Input 
                value={uploadForm.name}
                onChange={(e) => setUploadForm({...uploadForm, name: e.target.value})}
                placeholder={isSpanish ? 'Ej: Factura #12345' : 'e.g., Invoice #12345'}
              />
            </div>
            <div>
              <Label>{isSpanish ? 'Tipo de Documento' : 'Document Type'} *</Label>
              <Select 
                value={uploadForm.document_type} 
                onValueChange={(v) => setUploadForm({...uploadForm, document_type: v})}
              >
                <SelectTrigger><SelectValue placeholder={isSpanish ? 'Seleccionar' : 'Select'} /></SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {isSpanish ? type.label_es : type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{isSpanish ? 'URL del Archivo' : 'File URL'} *</Label>
              <Input 
                value={uploadForm.file_url}
                onChange={(e) => setUploadForm({...uploadForm, file_url: e.target.value})}
                placeholder="https://..."
              />
              <p className="text-xs text-steel-500 mt-1">
                {isSpanish 
                  ? 'Ingrese la URL del archivo alojado en su servidor o servicio de almacenamiento'
                  : 'Enter the URL of the file hosted on your server or storage service'}
              </p>
            </div>
            <div>
              <Label>{isSpanish ? 'Descripción' : 'Description'}</Label>
              <Input 
                value={uploadForm.description}
                onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{isSpanish ? 'Relacionado a (ID)' : 'Related To (ID)'}</Label>
                <Input 
                  value={uploadForm.related_to}
                  onChange={(e) => setUploadForm({...uploadForm, related_to: e.target.value})}
                  placeholder={isSpanish ? 'ID de cotización o envío' : 'Quote or Shipment ID'}
                />
              </div>
              <div>
                <Label>{isSpanish ? 'Tipo de Relación' : 'Relation Type'}</Label>
                <Select 
                  value={uploadForm.related_type} 
                  onValueChange={(v) => setUploadForm({...uploadForm, related_type: v})}
                >
                  <SelectTrigger><SelectValue placeholder={isSpanish ? 'Seleccionar' : 'Select'} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">-</SelectItem>
                    <SelectItem value="quote">{isSpanish ? 'Cotización' : 'Quote'}</SelectItem>
                    <SelectItem value="shipment">{isSpanish ? 'Envío' : 'Shipment'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUploadDialogOpen(false)}>
              {isSpanish ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={handleUploadDocument} className="btn-accent">
              <Upload className="w-4 h-4 mr-2" />
              {isSpanish ? 'Guardar' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Document Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isSpanish ? 'Detalles del Documento' : 'Document Details'}</DialogTitle>
          </DialogHeader>
          {selectedDocument && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4 p-4 bg-steel-50 rounded-lg">
                {(() => {
                  const typeInfo = getDocumentTypeInfo(selectedDocument.document_type);
                  const TypeIcon = typeInfo.icon;
                  return (
                    <>
                      <div className="w-12 h-12 bg-gold/20 rounded-lg flex items-center justify-center">
                        <TypeIcon className="w-6 h-6 text-gold" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-navy-800">{selectedDocument.name}</h3>
                        <p className="text-sm text-steel-500">{isSpanish ? typeInfo.label_es : typeInfo.label}</p>
                      </div>
                    </>
                  );
                })()}
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-steel-500">{isSpanish ? 'Subido por' : 'Uploaded by'}:</span>
                  <p className="font-medium">{selectedDocument.uploaded_by || '-'}</p>
                </div>
                <div>
                  <span className="text-steel-500">{isSpanish ? 'Fecha' : 'Date'}:</span>
                  <p className="font-medium">
                    {selectedDocument.created_at 
                      ? new Date(selectedDocument.created_at).toLocaleString()
                      : '-'}
                  </p>
                </div>
                <div>
                  <span className="text-steel-500">{isSpanish ? 'Relacionado a' : 'Related to'}:</span>
                  <p className="font-medium">{selectedDocument.related_to || '-'}</p>
                </div>
                <div>
                  <span className="text-steel-500">{isSpanish ? 'Tipo de Relación' : 'Relation type'}:</span>
                  <p className="font-medium">{selectedDocument.related_type || '-'}</p>
                </div>
              </div>
              
              {selectedDocument.description && (
                <div>
                  <span className="text-steel-500 text-sm">{isSpanish ? 'Descripción' : 'Description'}:</span>
                  <p className="mt-1">{selectedDocument.description}</p>
                </div>
              )}
              
              {selectedDocument.file_url && (
                <Button 
                  onClick={() => window.open(selectedDocument.file_url, '_blank')}
                  className="w-full btn-accent"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isSpanish ? 'Descargar Documento' : 'Download Document'}
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentManagementPage;
